import type {
  AuthNextSignInStep,
  AuthUserAttributeKey,
} from '@aws-amplify/auth/types';
import {Anchor} from 'src/design/atoms/Anchor';
import {Container} from 'src/design/atoms/Container';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {Link, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';

import {UserValidationStatusEnum} from 'src/api/ProactApiClient';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {ConfirmSignInWithCustomChallenge} from 'src/app/security/authentication/login-steps/ConfirmSignInWithCustomChallenge';
import {ConfirmSignInWithNewPasswordRequired} from 'src/app/security/authentication/login-steps/ConfirmSignInWithNewPasswordRequired';
import {SelectTotp} from 'src/app/security/authentication/login-steps/SelectTotp';
import {ConfirmSignInWithTotpCode} from 'src/app/security/authentication/login-steps/ConfirmSignInWithTotpCode';
import {ConfirmSignUp} from 'src/app/security/authentication/login-steps/ConfirmSignUp';
import {ContinueSignInWithTotpSetup} from 'src/app/security/authentication/login-steps/ContinueSignInWithTotpSetup';
import {DomainCheck} from 'src/app/security/authentication/login-steps/DomainCheck';
import {IdentityProviderRedirect} from 'src/app/security/authentication/login-steps/IdentityProviderRedirect';
import {InitialSignIn} from 'src/app/security/authentication/login-steps/InitialSignIn';
import {InstancePicker} from 'src/app/security/authentication/InstancePicker';
import {ResetPassword} from 'src/app/security/authentication/login-steps/ResetPassword';
import {
  resetAuthErrors,
  resetAuthState,
  resetSignInNextStep,
  setIsDomainChecked,
} from 'src/redux/slices/authSlice';

import {useEffect, useState} from 'react';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {
  getUserInstances,
  setActiveInstance,
  setIsSessionInitiailized,
  setIsTenantInitiailized,
  validateUserSession,
} from 'src/redux/slices/sessionSlice';
import {Spinner} from 'src/design/atoms/Spinner';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const RETURN_URL_KEY = 'proact_post_login_return_url';

const LoginPage = () => {
  useSetDocumentTitle('Login');

  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const {
    isError,
    message,
    isDomainChecked,
    identityProvider,
    signInNextStep,
    userAccountStatus,
  } = useAppSelector(state => state.auth);

  const {instance, instances, isInstanceListLoading, isInstanceLoading} =
    useAppSelector(state => state.session);

  const [showInstancePicker, setShowInstancePicker] = useState(false);
  const [isLoadingPostAuth, setIsLoadingPostAuth] = useState(false);
  const [postAuthError, setPostAuthError] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }
  }, [searchParams]);

  const getPostLoginRedirectUrl = (): string => {
    const storedUrl = sessionStorage.getItem(RETURN_URL_KEY);
    if (storedUrl && storedUrl.startsWith('/') && !storedUrl.startsWith('//')) {
      sessionStorage.removeItem(RETURN_URL_KEY);
      return storedUrl;
    }
    return PathUtility.Dashboard();
  };

  useEffect(() => {
    if (isError) {
      dispatch(resetAuthErrors());
    }
  }, []);

  const handleRetryDomainCheck = () => {
    dispatch(setIsDomainChecked(false));
    dispatch(resetSignInNextStep());
  };

  const handlePostAuthComplete = async () => {
    setIsLoadingPostAuth(true);
    try {
      await dispatch(validateUserSession()).unwrap();
      dispatch(setIsSessionInitiailized(true));
      const instanceList = await dispatch(getUserInstances()).unwrap();
      dispatch(setIsTenantInitiailized(true));

      if (instanceList.length === 0) {
        navigate(PathUtility.PermissionDenied(), {replace: true});
        return;
      }

      if (instanceList.length === 1) {
        navigate(getPostLoginRedirectUrl(), {replace: true});
        return;
      }

      setShowInstancePicker(true);
    } finally {
      setIsLoadingPostAuth(false);
    }
  };

  const handleInstanceSelected = async (instanceId: number) => {
    setPostAuthError(undefined);
    try {
      await dispatch(setActiveInstance(instanceId)).unwrap();
    } catch (error) {
      setPostAuthError(parseErrorMessage(error));
      return;
    }
    navigate(getPostLoginRedirectUrl(), {replace: true});
  };

  useEffect(() => {
    if (signInNextStep?.signInStep === 'DONE' && !showInstancePicker) {
      handlePostAuthComplete().catch(error =>
        setPostAuthError(parseErrorMessage(error))
      );
    }
  }, [signInNextStep, showInstancePicker]);

  function getSignInStep(
    signInNextStep: AuthNextSignInStep<AuthUserAttributeKey> | null
  ): React.ReactNode {
    if (showInstancePicker) {
      return (
        <InstancePicker
          instances={instances}
          currentInstanceId={instance?.id}
          onSelect={handleInstanceSelected}
          onBack={handleRetryDomainCheck}
          isLoading={isInstanceLoading}
        />
      );
    }

    if (isLoadingPostAuth || isInstanceListLoading) {
      return <Spinner loadingText="Loading your account..." />;
    }

    // Step 1: Domain check (always first)
    if (isDomainChecked === false && !signInNextStep) {
      return <DomainCheck />;
    }

    // Step 2: Handle identity provider redirect
    if (isDomainChecked === true && identityProvider !== null) {
      return <IdentityProviderRedirect />;
    }

    // Step 3: Intelligent flow based on user account status
    if (
      isDomainChecked === true &&
      userAccountStatus !== null &&
      !signInNextStep
    ) {
      switch (userAccountStatus) {
        case UserValidationStatusEnum.Valid:
          return <InitialSignIn />;

        case UserValidationStatusEnum.DoesNotExist:
          return (
            <Stack>
              <Text size="sm" c="dimmed" ta="center">
                No account found with this email address. If you're sure you
                have the right email address, your account may have been
                deleted.
              </Text>
              <Anchor
                size="sm"
                component="button"
                onClick={handleRetryDomainCheck}
                ta="center">
                Try a different email
              </Anchor>
            </Stack>
          );

        case UserValidationStatusEnum.Inactive:
          return (
            <Stack>
              <Text size="sm" c="dimmed" ta="center">
                Your account is currently inactive. Contact your administrator
                to unlock your account and login.
              </Text>
            </Stack>
          );

        case UserValidationStatusEnum.OAuthUnconfirmed:
          return <ConfirmSignUp />;

        case UserValidationStatusEnum.OAuthForcedChangePassword:
          return (
            <Stack>
              <Text size="sm" c="dimmed" ta="center">
                Your account has been reset and is currently unconfirmed. Please
                check your email for a confirmation link.
              </Text>
            </Stack>
          );

        case UserValidationStatusEnum.OAuthArchived:
        case UserValidationStatusEnum.OAuthCompromised:
          return (
            <Stack>
              <Text size="sm" c="dimmed" ta="center">
                Your account has been locked for security reasons. Contact your
                administrator to resolve the issue and login.
              </Text>
            </Stack>
          );

        case UserValidationStatusEnum.OAuthResetRequired:
          return <ResetPassword />;

        case UserValidationStatusEnum.OAuthUnknown:
        default:
          return <InitialSignIn />;
      }
    }

    // Step 4: Handle AWS Cognito sign-in steps
    if (signInNextStep == null) {
      return <InitialSignIn />;
    }

    switch (signInNextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
        return <ConfirmSignInWithCustomChallenge />;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
        return <SelectTotp />;
      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        return (
          <Text>
            SMS authentication is not supported. Contact support to recover your
            account.
          </Text>
        );
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        return <ConfirmSignInWithTotpCode />;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        return <ContinueSignInWithTotpSetup />;
      case 'CONFIRM_SIGN_UP':
        return <ConfirmSignUp />;
      case 'RESET_PASSWORD':
        return <ResetPassword />;
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return <ConfirmSignInWithNewPasswordRequired />;
      case 'DONE':
        return <Spinner loadingText="Loading your account..." />;

      default:
        navigate(PathUtility.NotFound());
    }
  }

  return (
    <Container size={450} my={40}>
      <Stack>
        <Title ta="center">Welcome back!</Title>
        <Text c="dimmed" size="sm" ta="center">
          Do not have an account yet?{' '}
          <Anchor size="sm" component={Link} to="https://proactehs.com/contact">
            Contact Us
          </Anchor>
        </Text>

        <Paper withBorder shadow="md" p={30} radius="md">
          {getSignInStep(signInNextStep)}
          <ServerErrorAlert message={message} />
          <ServerErrorAlert message={postAuthError} />
        </Paper>
        <Group justify="space-between">
          <Anchor
            c="dimmed"
            size="sm"
            onClick={() => {
              dispatch(resetAuthState());
            }}>
            ⟳ Reset email
          </Anchor>
          <Anchor
            c="dimmed"
            size="sm"
            component={Link}
            to={PathUtility.Support()}>
            Having trouble? →
          </Anchor>
        </Group>
      </Stack>
    </Container>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.Login(),
  element: <LoginPage />,
  isPrivate: false,
});
